import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { Pill } from '@shared/components/pill';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { FileUpload } from '@shared/types/fieldValues';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { isImage } from '@shared/util/media/type';
import { startViewTransition } from '@shared/util/startViewTransition';
import { FileItem } from './FileItem';
import styles from './FileList.module.css';

interface FilesProps {
  files: FileUpload[];
  maxFiles?: number;
  editable: boolean;
  showFileIcon?: boolean;
  onFileRemoved?: (file: FileUpload) => void;
  layout?: 'row' | 'column';
  showLayoutToggle?: boolean;
}

export function FileList({
  files,
  maxFiles = Infinity,
  onFileRemoved,
  editable,
  showFileIcon = true,
  layout = 'row',
  showLayoutToggle = false,
}: FilesProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const nonImages = useMemo(() => files.filter(file => !isImage(file)), [files]);
  const filesToShow = useMemo(() => nonImages.slice(0, maxFiles), [nonImages, maxFiles]);

  const [renderFiles, setRenderFiles] = useState(filesToShow);
  const [isColumnLayout, , , toggleColumnLayout] = useBoolean(layout === 'column');

  useEffect(() => {
    if (listRef.current) {
      startViewTransition(() => {
        setRenderFiles(filesToShow);
      }, listRef.current);
    }
  }, [filesToShow]);

  if (isEmpty(nonImages)) {
    return null;
  }

  const toggleLayoutTooltip = isColumnLayout ? i18n('common.collapse') : i18n('common.expand');

  return (
    <View row gap={0.5} flex paddingLeft={showLayoutToggle ? 0 : 0.5}>
      {showLayoutToggle && (
        <View stopPropagation paddingTop={0.75}>
          <AnchoredTooltip tooltip={toggleLayoutTooltip}>
            <Button
              variant="ghost"
              size="medium"
              icon="arrow-point-right"
              iconOnly
              onClick={toggleColumnLayout}
              className={[styles.columnToggleButton, isColumnLayout && styles.isColumn]}
            />
          </AnchoredTooltip>
        </View>
      )}
      <View
        gap={1}
        className={[
          styles.fileList,
          !isColumnLayout && styles.row,
          isColumnLayout && styles.column,
        ]}
        ref={listRef}
      >
        {renderFiles.map(item => (
          <FileItem
            key={item.url}
            file={item}
            editable={editable}
            showFileIcon={showFileIcon}
            onClear={() => onFileRemoved?.(item)}
          />
        ))}
        <FileListFooter files={nonImages} maxFiles={maxFiles} />
      </View>
    </View>
  );
}

function FileListFooter({ files, maxFiles }: { files: FileUpload[]; maxFiles: number }) {
  const extraFilesCount = files.length - maxFiles;
  if (extraFilesCount < 1) {
    return null;
  }
  return <Pill noLeftMargin>{`+${extraFilesCount}`}</Pill>;
}
